import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useAuth } from './AuthContext'
import UserService from '../features/profile/services/UserService'

const NotificationContext = createContext()

const STORAGE_KEY = 'rustaro_notification_state'

export const useNotificationContext = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error(
      'useNotificationContext debe usarse dentro de un NotificationProvider',
    )
  }
  return context
}

const DEFAULT_PREFERENCES = {
  weather: true,
  overdue: true,
  lowStock: true,
  harvest: true,
  vehicleMaintenance: true,
  toolMaintenance: true,
}

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    // ignore
  }
  return {}
}

function persistState(state) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        dismissedIds: state.dismissedIds,
        lastOpenedAt: state.lastOpenedAt,
        preferences: state.preferences,
      }),
    )
  } catch {
    // ignore
  }
}

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const persisted = useRef(loadPersistedState())
  const backendLoaded = useRef(false)

  const [state, setState] = useState({
    notifications: [],
    panelOpen: false,
    lastOpenedAt: persisted.current.lastOpenedAt || null,
    dismissedIds: persisted.current.dismissedIds || [],
    preferences: { ...DEFAULT_PREFERENCES, ...(persisted.current.preferences || {}) },
  })

  const { dismissedIds, lastOpenedAt, preferences } = state

  // Load preferences from backend (override localStorage if available)
  useEffect(() => {
    if (!isAuthenticated) return
    const load = async () => {
      try {
        const prefs = await UserService.getPreferences()
        if (prefs?.notificationPreferences) {
          const parsed =
            typeof prefs.notificationPreferences === 'string'
              ? JSON.parse(prefs.notificationPreferences)
              : prefs.notificationPreferences
          setState((prev) => ({
            ...prev,
            preferences: { ...DEFAULT_PREFERENCES, ...parsed },
          }))
          backendLoaded.current = true
        }
      } catch {
        // fallback to localStorage is already loaded
      }
    }
    load()
  }, [isAuthenticated])

  useEffect(() => {
    persistState({ dismissedIds, lastOpenedAt, preferences })
  }, [dismissedIds, lastOpenedAt, preferences])

  // Also sync preferences to backend when they change
  useEffect(() => {
    if (!isAuthenticated || !backendLoaded.current) return
    UserService.updatePreferences({
      notificationPreferences: JSON.stringify(preferences),
    }).catch(() => {
      // silent fail
    })
  }, [preferences, isAuthenticated])

  const setNotifications = useCallback((notifications) => {
    setState((prev) => ({
      ...prev,
      notifications,
    }))
  }, [])

  const openPanel = useCallback(() => {
    setState((prev) => ({
      ...prev,
      panelOpen: true,
      lastOpenedAt: new Date().toISOString(),
    }))
  }, [])

  const closePanel = useCallback(() => {
    setState((prev) => ({
      ...prev,
      panelOpen: false,
    }))
  }, [])

  const dismissNotification = useCallback((id) => {
    setState((prev) => ({
      ...prev,
      dismissedIds: [...prev.dismissedIds, id],
    }))
  }, [])

  const markAllAsRead = useCallback(() => {
    setState((prev) => ({
      ...prev,
      lastOpenedAt: new Date().toISOString(),
    }))
  }, [])

  const updatePreferences = useCallback((partial) => {
    setState((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, ...partial },
    }))
  }, [])

  // Compute derived values
  const unreadCount = state.notifications
    .filter((n) => !state.dismissedIds.includes(n.id))
    .filter((n) => {
      if (!state.lastOpenedAt) return true
      return new Date(n.generatedAt) > new Date(state.lastOpenedAt)
    }).length

  const urgentCount = state.notifications
    .filter((n) => n.priority === 0)
    .filter((n) => !state.dismissedIds.includes(n.id))
    .filter((n) => {
      if (!state.lastOpenedAt) return true
      return new Date(n.generatedAt) > new Date(state.lastOpenedAt)
    }).length

  const visibleNotifications = state.notifications.filter(
    (n) => !state.dismissedIds.includes(n.id),
  )

  const contextValue = {
    state,
    unreadCount,
    urgentCount,
    visibleNotifications,
    setNotifications,
    openPanel,
    closePanel,
    dismissNotification,
    markAllAsRead,
    updatePreferences,
  }

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  )
}
